import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for countdown timer functionality
 * @param {string|Date} expiryTime - The expiry timestamp
 * @returns {Object} { minutes, seconds, isExpired, isWarning, totalSeconds }
 */
const useCountdown = (expiryTime) => {
    const [timeRemaining, setTimeRemaining] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!expiryTime) {
            setTimeRemaining(0);
            return;
        }

        // Calculate initial remaining time
        const calculateRemaining = () => {
            const now = new Date().getTime();
            const expiry = new Date(expiryTime).getTime();
            const remaining = expiry - now;
            return Math.max(0, Math.floor(remaining / 1000)); // Convert to seconds
        };

        // Set initial time
        setTimeRemaining(calculateRemaining());

        // Update every second
        intervalRef.current = setInterval(() => {
            const remaining = calculateRemaining();
            setTimeRemaining(remaining);

            // Clear interval when expired
            if (remaining <= 0) {
                clearInterval(intervalRef.current);
            }
        }, 1000);

        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [expiryTime]);

    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    const isExpired = timeRemaining <= 0;
    const isWarning = timeRemaining > 0 && timeRemaining < 30; // Warning when < 30 seconds

    return {
        minutes,
        seconds,
        totalSeconds: timeRemaining,
        isExpired,
        isWarning,
    };
};

export default useCountdown;
